import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle2, Download, FileSignature } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicAgreementPage() {
  const { token } = useParams();
  const [agr, setAgr] = useState(null);
  const [state, setState] = useState("loading");
  const [name, setName] = useState("");
  const [aadhaar, setAadhaar] = useState("");
  const [aadFile, setAadFile] = useState(null);
  const [agreeChk, setAgreeChk] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetch(`${API}/api/public/agreements/${token}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => { setAgr(d); setState(d.status === "ACCEPTED" ? "accepted" : "ready"); })
      .catch(() => setState("notfound"));
  }, [token]);

  const accept = async () => {
    setErr("");
    if (name.trim().length < 2) { setErr("Please enter your full name"); return; }
    const digits = aadhaar.replace(/\D/g, "");
    if (digits.length !== 12) { setErr("Please enter your 12-digit Aadhaar number"); return; }
    if (!aadFile) { setErr("Please upload your Aadhaar card photo/PDF"); return; }
    if (!agreeChk) { setErr("Please tick the confirmation box"); return; }
    setState("submitting");
    try {
      const r = await fetch(`${API}/api/public/agreements/${token}/accept`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accepted_by: name.trim(), aadhaar_number: digits, aadhaar_file: aadFile.data, aadhaar_file_type: aadFile.type }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || ""); }
      setAgr({ ...agr, status: "ACCEPTED", accepted_by: name.trim() });
      setState("accepted");
    } catch (e) { setState("ready"); setErr(e.message || "Something went wrong. Please try again."); }
  };

  const onAadFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setErr("File too large — max 8MB"); return; }
    const ok = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!ok.includes(file.type)) { setErr("Upload JPG, PNG, WEBP or PDF only"); return; }
    const reader = new FileReader();
    reader.onload = () => { setAadFile({ data: reader.result, type: file.type, name: file.name }); setErr(""); };
    reader.readAsDataURL(file);
  };

  if (state === "loading") return <div className="min-h-screen flex items-center justify-center text-gray-500 animate-pulse">Loading agreement…</div>;
  if (state === "notfound") return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center" data-testid="agr-public-notfound">
        <h1 className="text-2xl font-bold text-gray-900">Agreement not found</h1>
        <p className="text-gray-500 mt-2">This link may be invalid or expired. Contact BrandBorn at +91-9464645491.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <Helmet><title>{`Agreement ${agr.agreement_id} · BrandBorn`}</title><meta name="robots" content="noindex" /></Helmet>
      <div className="max-w-3xl mx-auto">
        <div className="bg-gray-900 text-white rounded-t-2xl p-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-black text-xl">Brand<span className="text-red-500">Born</span></div>
            <div className="text-gray-300 text-sm mt-1">Service Agreement · {agr.agreement_id} · {agr.platform}</div>
          </div>
          <a href={`${API}/api/public/agreements/${token}/pdf`} data-testid="agr-public-pdf" className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full text-sm font-semibold transition"><Download size={15} /> Download PDF</a>
        </div>

        {agr.status === "ACCEPTED" ? (
          <div className="bg-green-50 border-x border-green-200 p-5 flex items-center gap-3" data-testid="agr-public-accepted-banner">
            <CheckCircle2 className="text-green-600 shrink-0" size={26} />
            <div>
              <div className="font-bold text-green-800">Agreement Accepted</div>
              <div className="text-sm text-green-700">Accepted by {agr.accepted_by}{agr.accepted_at ? ` on ${agr.accepted_at.slice(0, 10)}` : ""}. A copy is available via Download PDF above.</div>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border-x border-amber-200 p-4 text-sm text-amber-800 font-semibold" data-testid="agr-public-pending-banner">
            ⏳ Pending acceptance — please review the agreement below and accept at the bottom.
          </div>
        )}

        <div className="bg-white border-x border-gray-200 p-6 md:p-10">
          <div className="prose prose-sm max-w-none whitespace-pre-wrap font-serif text-gray-800 text-[15px] leading-7" data-testid="agr-public-content">
            {agr.content.split("\n").map((line, i) => {
              if (line.trim() === "{{SIGNATURE}}") return <div key={i} className="italic text-gray-400 text-xs">[BrandBorn authorised signature — appears on the PDF]</div>;
              if (line.startsWith("# ")) return <h2 key={i} className="text-lg font-bold text-gray-900 mt-6 mb-2">{line.slice(2)}</h2>;
              return <p key={i} className="my-1">{line.split("**").map((seg, j) => (j % 2 ? <b key={j}>{seg}</b> : seg))}</p>;
            })}
          </div>
        </div>

        {agr.status !== "ACCEPTED" && (
          <div className="bg-white border border-gray-200 rounded-b-2xl p-6 space-y-4 shadow-lg" data-testid="agr-public-accept-box">
            <div className="flex items-center gap-2 font-bold text-gray-900"><FileSignature size={18} className="text-red-500" /> Accept this Agreement (Aadhaar verified e-sign)</div>
            <input data-testid="agr-public-name" className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-red-400 focus:outline-none" placeholder="Your full name (authorised signatory)" value={name} onChange={(e) => setName(e.target.value)} />
            <input data-testid="agr-public-aadhaar" inputMode="numeric" className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-red-400 focus:outline-none tracking-widest"
              placeholder="Aadhaar number — 12 digits (XXXX XXXX XXXX)" value={aadhaar}
              onChange={(e) => { const d = e.target.value.replace(/\D/g, "").slice(0, 12); setAadhaar(d.replace(/(\d{4})(?=\d)/g, "$1 ")); }} />
            <div>
              <label data-testid="agr-public-aadhaar-upload" className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition ${aadFile ? "border-green-400 bg-green-50" : "border-gray-300 hover:border-red-300"}`}>
                <span className={`text-sm font-semibold ${aadFile ? "text-green-700" : "text-gray-500"}`}>
                  {aadFile ? `✓ ${aadFile.name}` : "🪪 Upload Aadhaar card (photo or PDF) — required"}
                </span>
                <span className="text-xs font-bold text-red-500 shrink-0">{aadFile ? "Change" : "Choose file"}</span>
                <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={onAadFile} />
              </label>
            </div>
            <label className="flex items-start gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" data-testid="agr-public-checkbox" className="mt-1" checked={agreeChk} onChange={(e) => setAgreeChk(e.target.checked)} />
              I confirm I am authorised to sign on behalf of <b>{agr.company_name}</b> and I accept the terms of this agreement.
            </label>
            {err && <div className="text-sm text-red-500" data-testid="agr-public-error">{err}</div>}
            <button data-testid="agr-public-accept-btn" disabled={state === "submitting"} onClick={accept}
              className="w-full bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white font-bold py-4 rounded-xl transition">
              {state === "submitting" ? "Recording acceptance…" : "✓ Accept Agreement"}
            </button>
            <p className="text-xs text-gray-400 text-center">Your name, Aadhaar number, Aadhaar document, date and IP address will be recorded as legal proof of acceptance.</p>
          </div>
        )}
        {agr.status === "ACCEPTED" && <div className="bg-white border border-gray-200 rounded-b-2xl p-4 text-center text-xs text-gray-400">BrandBorn · www.brandborn.in · +91-9464645491</div>}
      </div>
    </div>
  );
}
